(() => {
  'use strict';

  const ROOT_SELECTOR = '.bm_customMountain_root[data-bm-product-options-cart-enabled="true"]';
  const GPX_UPLOAD_ID_PROPERTY = '_BM GPX Upload-ID';
  const GPX_FILE_PROPERTY = 'GPS-Track';
  function parseJsonElement(element, fallback) {
    if (!element?.textContent) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch {
      return fallback;
    }
  }

  function toSafePositiveInteger(value) {
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : null;
  }

  function formatMoney(cents, currencyCode) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '–';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: currencyCode || 'EUR'
      }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(2)} ${currencyCode || 'EUR'}`;
    }
  }

  function getProperties(form) {
    const properties = {};
    const formData = new FormData(form);

    for (const [name, value] of formData.entries()) {
      const match = /^properties\[(.+)]$/.exec(name);
      if (!match || typeof value !== 'string') continue;
      properties[match[1]] = value;
    }

    return properties;
  }

  function getAddonItems(root) {
    const items = [];

    root.querySelectorAll('[data-bm-product-options-item]').forEach(item => {
      const key = String(item.dataset.bmProductOptionsItem || '');
      if (key === 'snowModeling') return;

      const required = item.dataset.bmProductOptionsRequired === 'true';
      const input = item.querySelector('[data-bm-product-options-property]');
      const selected = required || input?.checked === true;
      if (!selected) return;

      const variantId = toSafePositiveInteger(item.dataset.bmProductOptionsAddonVariantId);
      const priceCents = Number(item.dataset.bmProductOptionsPriceCents);
      const name = item.querySelector('.bm_productOptions_name')?.textContent?.trim() || 'Zusatzleistung';

      items.push({
        element: item,
        key,
        variantId,
        priceCents: Number.isFinite(priceCents) ? priceCents : null,
        name,
        cartItem: item.dataset.bmProductOptionsCartItem !== 'false'
      });
    });

    return items;
  }

  function getGpxFormProperty(form, propertyName) {
    return Array.from(form?.querySelectorAll?.('input[data-bm-gpx-property]') || [])
      .find(input => input.dataset.bmGpxProperty === propertyName && !input.disabled)
      ?.value?.trim() || '';
  }

  function getGpxDetails(form) {
    const track = window.BM?.bm_peakExplorer_getGpxTrack?.() || null;
    const uploadId = getGpxFormProperty(form, GPX_UPLOAD_ID_PROPERTY)
      || String(track?.uploadId || '').trim();
    const fileName = getGpxFormProperty(form, GPX_FILE_PROPERTY)
      || String(track?.fileName || '').trim();
    const trackName = String(track?.trackName || '').trim();
    if (!track?.selected && !uploadId && !fileName) return null;

    return {
      fileName,
      trackName,
      uploadId,
      uploaded: Boolean(uploadId)
    };
  }

  function getAddonSummaryItems(addons, form) {
    const gpxDetails = getGpxDetails(form);
    const items = addons.map(addon => {
      if (addon.key !== 'gpx' || !gpxDetails) return addon;
      const displayName = gpxDetails.fileName || gpxDetails.trackName;
      return displayName ? { ...addon, name: `GPX-Track: ${displayName}` } : addon;
    });
    const snowSummaryItem = getSnowSummaryItem(form);
    return snowSummaryItem ? [...items, snowSummaryItem] : items;
  }

  function getSnowSummaryItem(form) {
    const snowEnabled = form.querySelector('[data-bm-role="snow-property"]')?.value === 'Ein';
    const snowlineM = Number(form.querySelector('[data-bm-role="snowline-property"]')?.value);

    if (!snowEnabled || !Number.isFinite(snowlineM) || snowlineM < 0) return null;

    return {
      name: `Schneemodellierung (${Math.round(snowlineM)} m)`,
      priceCents: 0,
      cartItem: false
    };
  }

  function findLinkedLayoutPart(root, partName) {
    const localPart = root.querySelector(`[data-bm-layout-part="${partName}"]`);
    if (localPart) return localPart;

    const productId = String(root.dataset.productId || '');
    if (!productId) return null;

    return Array.from(document.querySelectorAll(`[data-bm-layout-part="${partName}"]`))
      .find(part => String(part.dataset.bmProductId || '') === productId) || null;
  }

  function showError(errorBox, message) {
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.hidden = false;
    errorBox.setAttribute('tabindex', '-1');
    errorBox.focus({ preventScroll: true });
  }

  function clearError(errorBox) {
    if (!errorBox) return;
    errorBox.textContent = '';
    errorBox.hidden = true;
  }

  function createAddonSummary(items, currencyCode) {
    const list = document.createElement('ul');
    list.className = 'bm_customMountain_addonList';

    if (!items.length) {
      const item = document.createElement('li');
      item.textContent = 'Keine';
      list.appendChild(item);
      return list;
    }

    items.forEach(addon => {
      const item = document.createElement('li');
      item.className = 'bm_customMountain_addonListItem';
      const name = document.createElement('span');
      const price = document.createElement('span');
      name.textContent = addon.name;
      price.textContent = addon.priceCents === null
        ? 'Konfiguration fehlt'
        : addon.priceCents === 0
          ? 'Kostenlos'
          : formatMoney(addon.priceCents, currencyCode);
      item.append(name, price);
      list.appendChild(item);
    });

    return list;
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement) || root.dataset.bmProductOptionsCartInitialized === 'true') return;

    const formId = String(root.dataset.bmProductFormId || '').trim();
    const form = formId ? document.getElementById(formId) : null;
    const variantPrices = parseJsonElement(
      root.querySelector('script[type="application/json"][data-bm-variant-prices]'),
      {}
    );
    const currencyCode = root.dataset.bmCurrencyCode || 'EUR';
    const quantityInput = root.querySelector('[name="quantity"]');
    const summaryScope = findLinkedLayoutPart(root, 'order-summary');
    const totalElements = root.querySelectorAll('[data-bm-product-options-total]');
    const summaryBasePrice = summaryScope?.querySelector('[data-bm-summary="base-price"]');
    const summaryModelingDiscount = summaryScope?.querySelector('[data-bm-summary="modeling-discount"]');
    const summaryModelingDiscountRow = summaryScope?.querySelector('[data-bm-summary-row="modeling-discount"]');
    const summaryAddons = summaryScope?.querySelector('[data-bm-summary="addons"]');
    const summaryTotal = summaryScope?.querySelector('[data-bm-summary="total"]');
    const errorBox = summaryScope?.querySelector('[data-bm-role="validation-error"]');
    const submitButton = summaryScope?.querySelector('[data-bm-role="submit"]');
    const modelingDiscountCents = Number(root.dataset.bmModelingDiscountCents);

    if (!(form instanceof HTMLFormElement) || !(quantityInput instanceof HTMLInputElement)) return;
    root.dataset.bmProductOptionsCartInitialized = 'true';

    function getCurrentQuantity() {
      return toSafePositiveInteger(quantityInput.value) || 1;
    }

    function getMainVariantId() {
      return toSafePositiveInteger(form.querySelector('[name="id"]')?.value);
    }

    function getMainPriceCents() {
      const variantId = getMainVariantId();
      const price = variantId === null ? NaN : Number(variantPrices[String(variantId)]);
      return Number.isFinite(price) ? price : null;
    }

    function getModelingDiscountCents(quantity) {
      if (!Number.isFinite(modelingDiscountCents) || modelingDiscountCents <= 0 || quantity < 2) {
        return 0;
      }

      return (quantity - 1) * modelingDiscountCents;
    }

    function updateTotals() {
      const quantity = getCurrentQuantity();
      const mainPriceCents = getMainPriceCents();
      const addons = getAddonItems(root);
      const modelingDiscount = getModelingDiscountCents(quantity);
      const addonPriceCents = addons.reduce(
        (sum, addon) => sum + (!addon.cartItem || addon.priceCents === null ? 0 : addon.priceCents),
        0
      );
      const totalCents = mainPriceCents === null
        ? null
        : (mainPriceCents * quantity) - modelingDiscount + addonPriceCents;

      totalElements.forEach(element => {
        element.textContent = totalCents === null ? 'Preis wird aktualisiert …' : formatMoney(totalCents, currencyCode);
      });
      if (summaryBasePrice) {
        summaryBasePrice.textContent = mainPriceCents === null
          ? 'Preis wird aktualisiert …'
          : formatMoney(mainPriceCents * quantity, currencyCode);
      }
      if (summaryModelingDiscountRow) {
        summaryModelingDiscountRow.hidden = modelingDiscount === 0;
      }
      if (summaryModelingDiscount) {
        summaryModelingDiscount.textContent = modelingDiscount === 0
          ? '–'
          : `−${formatMoney(modelingDiscount, currencyCode)}`;
      }
      if (summaryAddons) {
        summaryAddons.replaceChildren(createAddonSummary(
          getAddonSummaryItems(addons, form),
          currencyCode
        ));
      }
      if (summaryTotal) {
        summaryTotal.textContent = totalCents === null ? 'Preis wird aktualisiert …' : formatMoney(totalCents, currencyCode);
      }
    }

    async function handleSubmit(event) {
      const addons = getAddonItems(root);
      const missingConfiguration = addons.find(addon => (
        addon.cartItem && (addon.variantId === null || addon.priceCents === null)
      ));

      if (missingConfiguration) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showError(
          errorBox,
          `„${missingConfiguration.name}“ ist derzeit nicht verfügbar. Bitte entferne diese Auswahl oder versuche es später erneut.`
        );
        return;
      }

      const selectedGpxAddon = addons.find(addon => addon.key === 'gpx');
      const gpxDetails = getGpxDetails(form);
      if (selectedGpxAddon && (!gpxDetails?.uploaded || !gpxDetails.fileName)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showError(
          errorBox,
          'Der GPX-Track wurde noch nicht sicher gespeichert. Bitte versuche den Upload erneut oder entferne die GPX-Zusatzleistung.'
        );
        return;
      }

      const mainVariantId = getMainVariantId();
      const quantity = getCurrentQuantity();
      if (mainVariantId === null) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showError(errorBox, 'Die gewählte Produktvariante ist ungültig. Bitte wähle die Ausführung erneut.');
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      clearError(errorBox);

      const originalButtonDisabled = submitButton?.disabled === true;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
      }

      const linkKey = `${mainVariantId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const mainProperties = getProperties(form);
      mainProperties._bm_zusatzleistungsschluessel = linkKey;

      const requestItems = [
        {
          id: mainVariantId,
          quantity,
          properties: mainProperties
        },
        ...addons.filter(addon => addon.cartItem).map(addon => {
          const properties = {
            _bm_zusatzleistungsschluessel: linkKey
          };
          if (addon.key === 'gpx' && gpxDetails) {
            const displayName = gpxDetails.fileName || gpxDetails.trackName;
            if (displayName) properties['GPX-Track'] = displayName;
            if (gpxDetails.trackName && gpxDetails.trackName !== displayName) {
              properties['GPX-Trackname'] = gpxDetails.trackName;
            }
            if (gpxDetails.uploadId) properties[GPX_UPLOAD_ID_PROPERTY] = gpxDetails.uploadId;
          }
          return {
            id: addon.variantId,
            quantity: 1,
            properties
          };
        })
      ];

      try {
        const response = await fetch(root.dataset.bmCartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: requestItems })
        });
        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(result?.description || result?.message || 'Die Artikel konnten nicht zum Warenkorb hinzugefügt werden.');
        }

        window.location.assign(root.dataset.bmCartUrl || '/cart');
      } catch (error) {
        showError(errorBox, error instanceof Error ? error.message : 'Der Warenkorb konnte nicht aktualisiert werden.');
        if (submitButton) {
          submitButton.disabled = originalButtonDisabled;
          submitButton.removeAttribute('aria-busy');
        }
      }
    }

    root.addEventListener('change', updateTotals);
    root.addEventListener('input', updateTotals);
    root.addEventListener('bm:customMountain:snow-summary-change', updateTotals);
    document.addEventListener('bm:peakExplorer:gpx-change', updateTotals);
    form.addEventListener('change', updateTotals);
    form.addEventListener('submit', handleSubmit, true);
    updateTotals();

    root.bmProductOptionsCartDestroy = () => {
      root.removeEventListener('change', updateTotals);
      root.removeEventListener('input', updateTotals);
      root.removeEventListener('bm:customMountain:snow-summary-change', updateTotals);
      document.removeEventListener('bm:peakExplorer:gpx-change', updateTotals);
      form.removeEventListener('change', updateTotals);
      form.removeEventListener('submit', handleSubmit, true);
      delete root.dataset.bmProductOptionsCartInitialized;
      delete root.bmProductOptionsCartDestroy;
    };
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function reinitializeAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => root.bmProductOptionsCartDestroy?.());
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', reinitializeAll);
  document.addEventListener('shopify:section:unload', reinitializeAll);
})();
